import { COMMANDAPI, type ResultType } from '../constants/global';

/**
 * Which api.command result-create endpoint each level submits to.
 *
 * The V2 endpoints are the entry points to the MassTransit saga pipeline: posting to one
 * starts a state machine that does SQL projection -> agent callback -> AI document
 * extraction -> (polling unit only) iREV fetch, and records per-stage progress plus a
 * validation verdict. The V1 endpoints write the result and stop. Nothing else differs.
 *
 * For **Ward, LGA and State the two versions bind the identical backend DTO** — verified
 * against the deployed OpenAPI spec — so switching those levels is a URL change and nothing
 * more. **Polling unit is the only level with a real payload delta**: V2 additionally
 * requires the full `election` entity and six nested `{code,name}` objects. That is why this
 * is a per-level map and not a single boolean: it makes "pin polling unit to v1, leave the
 * rest on v2" a one-line change.
 *
 * All four collation levels now default to V2. The polling-unit payload is assembled by
 * `buildPuResultV2` (`upload-result/pu-payload.ts`) and gated by `missingV2Fields`, so a
 * blank division object blocks Submit rather than reaching the saga.
 */
export type ResultApiVersion = 'v1' | 'v2';

/**
 * Build-time default per level. Flipping an entry is a one-line commit -> CI date-serial
 * build -> redeploy, which is the primary rollback lever (same latency as redeploying a
 * previous image tag, but per-level, so a fault at one level doesn't revert the others).
 */
const DEFAULT_VERSION: Record<ResultType, ResultApiVersion> = {
	EC8A: 'v2',
	EC8B: 'v2',
	EC8C: 'v2',
	EC8D: 'v2',
	EC8E: 'v1',
	EC4OH: 'v1',
	EC4OG: 'v1',
	EC4OA: 'v1',
	EC4OB: 'v1',
	EC4OC: 'v1',
};

/**
 * Runtime override, for support and QA only — agents cannot open devtools, so do not treat
 * this as a field-facing control. It exists because Vite `define` bakes env vars at build
 * time, so an env var could not be flipped during an election.
 *
 * Accepts either a bare version applying to every level (`v1`) or a comma-separated list of
 * per-level pins (`EC8A:v1,EC8B:v2`). Anything unparseable is ignored.
 */
const OVERRIDE_KEY = 'ielect-result-api-version';

const readOverride = (): string => {
	try {
		return localStorage.getItem(OVERRIDE_KEY)?.trim() ?? '';
	} catch {
		// Safari private mode throws on localStorage access.
		return '';
	}
};

const isVersion = (value: string): value is ResultApiVersion => value === 'v1' || value === 'v2';

/**
 * Resolved on every call rather than at module load, so a support override takes effect on
 * the next Submit tap without a reload.
 */
export const resultApiVersion = (level: ResultType): ResultApiVersion => {
	const override = readOverride();

	if (isVersion(override)) return override;

	if (override.includes(':')) {
		for (const entry of override.split(',')) {
			const [key, value] = entry.split(':').map((part) => part.trim());
			if (key === level && isVersion(value)) return value;
		}
	}

	return DEFAULT_VERSION[level] ?? 'v1';
};

/**
 * Path segment per level. Note `local_government` carries an underscore while every other
 * segment is plain — that asymmetry is the backend's, and it is the single easiest thing to
 * get wrong here, so it lives in exactly one place.
 */
const CREATE_PATH: Partial<Record<ResultType, string>> = {
	EC8A: 'PollingUnitResult/create-polling-unit-result',
	EC8B: 'WardResult/create-ward-result',
	EC8C: 'LocalGovernmentResult/create-local_government-result',
	EC8D: 'StateResult/create-state-result',
};

/**
 * Full create URL for a level, honouring the version switch. V2 is the same path with a
 * `v2` suffix — there is no separate route shape.
 */
export const resultCreateUrl = (level: ResultType): string => {
	const path = CREATE_PATH[level];

	if (!path) throw new Error(`No result-create endpoint is defined for ${level}.`);

	return `${COMMANDAPI}/api/v1/i-elect/${path}${resultApiVersion(level) === 'v2' ? 'v2' : ''}`;
};
